import { SalesStage } from 'models/sales_stages'
import { colors } from 'styles/tokens'

/** Indigo gradient palette ordered from lightest to darkest, mapped by stage position. */
const INDIGO_GRADIENT = [
  '#C5CAE9', // Indigo 100
  '#9FA8DA', // Indigo 200
  '#8692D0', // Indigo 300
  '#5C6BC0', // Indigo 400
  '#3F51B5', // Indigo 500
  '#3949AB', // Indigo 600
  '#303F9F', // Indigo 700
  '#283593', // Indigo 800
  '#1A237E', // Indigo 900
]

/** Special stage colors keyed by lowercase stage name. */
const SPECIAL_STAGE_COLORS: Record<string, { bg: string; text: string }> = {
  'closed won': { bg: '#B9F6CA', text: colors['fg'] },
  'closed lost': { bg: '#FFCDD2', text: colors['fg'] },
  churn: { bg: '#78909C', text: colors['fg-inverse'] },
}

/** Terminal stage names that always appear at the end of the pipeline. */
const TERMINAL_STAGE_NAMES = new Set(['closed won', 'closed lost', 'churn'])

/** Indigo stages with index >= 2 use white text for readability. */
const DARK_TEXT_THRESHOLD = 2

/** Gray fill for inactive/placeholder stage chevrons. */
export const INACTIVE_STAGE_COLOR = colors['surface-disabled']

/** Default fallback color for stages with no pipeline color match. */
export const DEFAULT_STAGE_COLOR = INDIGO_GRADIENT[0]

/** Check if a stage is a terminal stage (Closed Won, Closed Lost, Churn). */
export function isTerminalStage(stage: SalesStage): boolean {
  return TERMINAL_STAGE_NAMES.has(stage.stage.toLowerCase())
}

/**
 * Sort stages into canonical pipeline order: custom order when enabled,
 * otherwise avg_days_to_close DESC (longest first = earliest pipeline stage).
 */
export function sortStagesPipelineOrder(
  stages: SalesStage[],
  customOrderEnabled: boolean
): SalesStage[] {
  return [...stages].sort((a, b) =>
    customOrderEnabled
      ? a.order - b.order
      : (b.avg_days_to_close ?? 0) - (a.avg_days_to_close ?? 0)
  )
}

/**
 * Get the fill color for a stage based on its position in the sorted list.
 * Special stages (Closed Won, Closed Lost, Churn) have fixed colors;
 * regular stages are mapped proportionally across the indigo gradient.
 */
export function getStageColor(
  stage: SalesStage,
  index: number,
  total: number
): { bg: string; text: string } {
  const special = SPECIAL_STAGE_COLORS[stage.stage.toLowerCase()]
  if (special) return special

  const gradientIndex =
    total <= 1
      ? 0
      : Math.round((index / (total - 1)) * (INDIGO_GRADIENT.length - 1))
  const bg = INDIGO_GRADIENT[gradientIndex]
  const text =
    gradientIndex >= DARK_TEXT_THRESHOLD ? colors['fg-inverse'] : colors['fg']
  return { bg, text }
}

/**
 * Get the pipeline color for a stage given the full list of stages.
 * Sorts stages into canonical pipeline order, finds the stage's position,
 * and returns the corresponding color. This ensures consistent colors
 * regardless of where the stage is rendered.
 *
 * @param stageId - ID of the stage to get the color for
 * @param allStages - Full list of stages (unsorted is fine)
 * @param customOrderEnabled - Whether custom ordering is active
 * @returns Color pair, or undefined if stage not found
 */
export function getPipelineColorForStage(
  stageId: number,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): { bg: string; text: string } | undefined {
  const sorted = sortStagesPipelineOrder(allStages, customOrderEnabled)
  const index = sorted.findIndex((s) => s.id === stageId)
  if (index === -1) return undefined
  return getStageColor(sorted[index], index, sorted.length)
}
